import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Pencil, Plus, RefreshCw, ShieldCheck, UserCheck, Users } from "lucide-react";
import type { Role, UserView } from "@ubisafe/shared";
import { UserFormModal } from "../components/UserFormModal";
import { api, ApiClientError } from "../lib/api";

interface UsersPageProps {
  currentUserId: string;
}

const roleLabels: Record<Role, string> = {
  ADMIN: "Administrador",
  PRE_IMPLEMENTATION: "Pré-implantação",
  FINANCE: "Financeiro",
};

export function UsersPage({ currentUserId }: UsersPageProps) {
  const [users, setUsers] = useState<UserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserView | undefined>();
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.listUsers();
      setUsers(response.users);
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível carregar os usuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const stats = useMemo(
    () => ({
      active: users.filter((user) => user.status === "ACTIVE").length,
      administrators: users.filter((user) => user.status === "ACTIVE" && user.roles.includes("ADMIN")).length,
    }),
    [users],
  );

  const saved = (user: UserView) => {
    setUsers((current) => {
      const exists = current.some((item) => item.id === user.id);
      const next = exists ? current.map((item) => (item.id === user.id ? user : item)) : [...current, user];
      return next.sort((left, right) => left.name.localeCompare(right.name, "pt-BR"));
    });
    setCreating(false);
    setEditing(undefined);
    setNotice("Usuário salvo com sucesso.");
    window.setTimeout(() => setNotice(null), 4_000);
  };

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <span className="eyebrow eyebrow--dark">Administração</span>
          <h1>Usuários</h1>
          <p>Controle contas locais, perfis e acesso ao sistema.</p>
        </div>
        <button className="primary-button" type="button" onClick={() => setCreating(true)}><Plus aria-hidden="true" /> Novo usuário</button>
      </section>

      <section className="compact-stats" aria-label="Resumo de usuários">
        <article><Users aria-hidden="true" /><div><strong>{users.length}</strong><span>Cadastrados</span></div></article>
        <article><UserCheck aria-hidden="true" /><div><strong>{stats.active}</strong><span>Ativos</span></div></article>
        <article><ShieldCheck aria-hidden="true" /><div><strong>{stats.administrators}</strong><span>Administradores ativos</span></div></article>
      </section>

      {notice && <div className="form-alert form-alert--success" role="status">{notice}</div>}

      <section className="content-card users-card">
        <div className="content-card__heading users-card__heading">
          <div><h2>Contas cadastradas</h2><p>O sistema sempre mantém ao menos um Administrador ativo.</p></div>
          <button className="icon-button" aria-label="Atualizar usuários" onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? "spin" : ""} /></button>
        </div>

        {error && (
          <div className="empty-state"><AlertCircle aria-hidden="true" /><h3>Não foi possível carregar</h3><p>{error}</p><button className="secondary-button" onClick={() => void load()}>Tentar novamente</button></div>
        )}

        {!error && loading && <div className="loading-list" aria-label="Carregando usuários"><span /><span /><span /></div>}

        {!error && !loading && users.length === 0 && (
          <div className="empty-state"><Users aria-hidden="true" /><h3>Nenhum usuário encontrado</h3><p>Cadastre a primeira conta operacional.</p></div>
        )}

        {!error && !loading && users.length > 0 && (
          <div className="responsive-table">
            <table>
              <thead><tr><th>Usuário</th><th>Perfis</th><th>Estado</th><th>Atualização</th><th><span className="sr-only">Ações</span></th></tr></thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td data-label="Usuário"><div className="user-cell"><div className="avatar avatar--light">{user.name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("")}</div><div><strong>{user.name}{user.id === currentUserId && <small className="you-label">Você</small>}</strong><span>{user.email}</span></div></div></td>
                    <td data-label="Perfis"><div className="role-chips">{user.roles.map((role) => <span className={`role-chip role-chip--${role.toLowerCase()}`} key={role}>{roleLabels[role]}</span>)}</div></td>
                    <td data-label="Estado"><span className={user.status === "ACTIVE" ? "status-badge status-badge--active" : "status-badge status-badge--disabled"}><i />{user.status === "ACTIVE" ? "Ativa" : "Desativada"}</span></td>
                    <td data-label="Atualização"><time dateTime={user.updatedAt}>{new Date(user.updatedAt).toLocaleDateString("pt-BR")}</time></td>
                    <td className="table-action"><button className="secondary-button secondary-button--small" onClick={() => setEditing(user)}><Pencil aria-hidden="true" /> Editar</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creating && <UserFormModal onClose={() => setCreating(false)} onSaved={saved} />}
      {editing && <UserFormModal user={editing} onClose={() => setEditing(undefined)} onSaved={saved} />}
    </div>
  );
}
